<template>
  <ValidationObserver
    class="studio-product-checkout"
    @submit.prevent="purchase"
    tag="form"
    ref="form"
    v-slot="{ invalid }"
  >
    <div class="row">
      <div
        class="offset-md-2 offset-lg-2 col-12 col-md-8 col-lg-5 mt-2 mt-lg-4 p-lg-0"
      >
        <slot name="breadcrumbs" :product="product" v-if="product"></slot>
        <div class="loading w-100 text-center mt-4" v-if="loading">
          <HMSpinner />
        </div>
        <div v-if="product">
          <div
            class="mobile-product-container d-block d-lg-none mt-3 bg-lightest-gray p-3"
          >
            <div
              class="d-flex flex-column flex-sm-row justify-content-start align-items-start flex-nowrap"
            >
              <HMFixedAspectRatioContainer class="img-container mobile">
                <ProductThumbnail :product="product" :hideOverlay="true" />
              </HMFixedAspectRatioContainer>
              <div
                class="d-flex flex-column justify-content-start align-items-start ml-sm-2 mt-2"
              >
                <span class="c-charcoal">{{ product.product_name }}</span>
                <span
                  class="smaller mt-2"
                  :class="{
                    'price-line-through': wishPriceSelected,
                  }"
                  v-if="getSelectedPriceOption"
                  v-html="
                    sanitize(
                      getSelectedPriceOption.selectedTitle ||
                        getSelectedPriceOption.html
                    )
                  "
                ></span>
              </div>
            </div>
          </div>
          <h2 class="my-4">{{ $t('PRODUCTS.LABELS.TYPE_OF_PURCHASE') }}</h2>
          <div class="price-options">
            <div
              class="price-option-container"
              v-for="option in priceOptions"
              :key="option.value"
            >
              <HMRadio
                class="price-option"
                v-model="selectedPriceOption"
                group="price-options"
                :name="option.value"
              >
                <span v-if="option.text">{{ option.text }}</span>
                <span v-if="option.html" v-html="sanitize(option.html)"></span>
              </HMRadio>
            </div>
          </div>
          <!-- Gifting START -->
          <div class="row mt-5">
            <div class="col-12">
              <h2 class="mb-4">
                {{ $t('PRODUCTS.TITLE.GIFT_INFORMATION') }}
              </h2>
            </div>
            <div class="col-12 col-md-6">
              <HMTextInput
                :label="$t('PRODUCTS.LABELS.RECIPIENT_S_FIRST_NAME')"
                v-model="recipient.firstName"
                rules="required"
              />
            </div>
            <div class="col-12 col-md-6">
              <HMTextInput
                :label="$t('PRODUCTS.LABELS.RECIPIENT_S_LAST_NAME')"
                v-model="recipient.lastName"
                rules="required"
              />
            </div>
            <div class="col-12">
              <HMTextInput
                :label="$t('PRODUCTS.LABELS.RECIPIENT_S_EMAIL')"
                v-model="recipient.email"
                rules="required"
                type="email"
                autocomplete="email"
              />
            </div>
            <div class="col-12">
              <HMTextarea
                :label="$t('PRODUCTS.LABELS.GIFT_MESSAGE_OPTIONAL')"
                v-model="recipient.message"
              />
            </div>
            <div class="col-12">
              <HMCheckBox v-model="gifter.anonymous">
                {{ $t('PRODUCTS.TEXT.MAKE_MY_GIFT_ANONYMOUS_TO_RECIPIENT') }}
              </HMCheckBox>
            </div>
            <div class="col-12">
              <hr class="my-4" />
              <div class="d-flex align-items-center w-100">
                <span class="calendar-icon mr-2 flex-shrink-0">
                  <i class="far fa-calendar"></i>
                </span>
                <span class="flex-grow-1 c-charcoal">
                  {{ $t('PRODUCTS.LABELS.SENDING') }}:
                  <b v-if="!giftDate.startDate">
                    {{ $t('PRODUCTS.LABELS.IMMEDIATELY') }}
                  </b>
                  <b v-else>
                    {{ $formatDate(giftDate.startDate, 'date-word') }}
                  </b>
                </span>
                <HMDateSelect v-model="giftDate" :showClear="true">
                  <a class="c-brand-color" role="button">
                    <span v-if="giftDate.startDate">
                      {{ $t('PRODUCTS.LINK.EDIT_DELIVERY_DATE') }}
                    </span>
                    <span v-else>
                      {{ $t('PRODUCTS.LINK.SCHEDULE_LATER_DELIVERY') }}
                    </span>
                  </a>
                </HMDateSelect>
              </div>
            </div>
            <div class="col-12" v-if="giftDatInvalid">
              <span class="alert-danger rounded p-2 px-3 mt-3 d-block">
                {{ $t('PRODUCTS.ERROR.PRODUCT_WON_T_BE_ACCESSIBLE') }}
              </span>
            </div>
          </div>
          <hr class="mt-4" />
          <div v-if="isLoggedIn && user" class="currently-logged-in mt-4">
            <HMUserSection
              @click="logout"
              :title="$t('GENERAL.LABEL.LOGGED_IN_AS')"
              :subtitle="user.user_full_name"
              :actionText="$t('GENERAL.LABEL.LOG_OUT_CHOOSE_DIFFERENT_ACCOUNT')"
            />
          </div>
          <div
            v-else-if="!isLoggedIn && !user && !continueAsGuest"
            class="mt-4 row log-in-or-register"
          >
            <div class="col-12 col-md-6">
              <HMUserSection
                @click="openLoginModal"
                :title="$t('PRODUCTS.LABELS.LOG_IN')"
                :subtitle="$t('GENERAL.LABEL.ALREADY_HAVE_AN_ACCOUNT')"
                :actionText="$t('GENERAL.BUTTON.LOG_IN')"
              />
            </div>
            <div class="col-12 col-md-6">
              <HMUserSection
                @click="openRegisterModal"
                :title="$t('GENERAL.LABEL.REGISTRATION')"
                :subtitle="
                  $t('PRODUCTS.LABELS.CREATE_NEW_ACCOUNT_AND_CONTINUE')
                "
                :actionText="$t('GENERAL.LABEL.CREATE_AN_ACCOUNT')"
              />
            </div>
            <div class="col-12 mt-3">
              <a @click="continueAsGuest = true" role="button">
                <i class="far fa-long-arrow-alt-right c-brand-color mr-2"></i>
                {{ $t('PRODUCTS.LINK.CONTINUE_AS_GUEST') }}
              </a>
            </div>
          </div>
          <div class="row mt-5" v-else>
            <div class="col-12 col-md-6">
              <HMTextInput
                :label="$t('PRODUCTS.LABELS.YOUR_FIRST_NAME')"
                v-model="gifter.firstName"
                rules="required"
              />
            </div>
            <div class="col-12 col-md-6">
              <HMTextInput
                :label="$t('PRODUCTS.LABELS.YOUR_LAST_NAME')"
                v-model="gifter.lastName"
                rules="required"
              />
            </div>
            <div class="col-12">
              <HMTextInput
                class="mb-0"
                :label="$t('PRODUCTS.LABELS.YOUR_EMAIL')"
                v-model="gifter.email"
                rules="required"
                type="email"
                autocomplete="email"
              />
            </div>
          </div>
          <!-- Gifting END -->
          <div class="billing-information-container mt-4">
            <div
              v-if="isStripeProcessor"
              v-show="(isLoggedIn || continueAsGuest) && !isForeverFree"
            >
              <hr class="mt-4" />
              <h2>
                {{ $t('PRODUCTS.LABELS.BILLING_INFORMATION') }}
              </h2>
              <div
                v-if="existingCreditCard"
                class="form-group mb-0 d-flex flex-row justify-content-start align-items-center"
              >
                <span class="mr-3">
                  {{ $t('PRODUCTS.LABELS.USE_EXISTING_CARD') }}
                </span>
                <HMSwitch v-model="useExistingCustomerData" />
              </div>
              <div
                v-show="useExistingCustomerData"
                v-html="existingCreditCardText"
              ></div>
              <div v-show="!useExistingCustomerData" class="form-group">
                <label class="mt-3" for="card-element">
                  {{ $t('SIGNUP.LABEL.CREDIT_CARD') }}
                </label>
                <div
                  id="card-element"
                  ref="card-element"
                  :class="{ 'is-invalid': stripeError }"
                ></div>
                <div class="invalid-feedback" v-if="stripeError">
                  {{ stripeError }}
                </div>
              </div>
            </div>
            <div
              class="d-block d-lg-none form-group mb-3"
              v-if="canShowCouponCode"
            >
              <label for="coupon">
                {{ $t('SIGNUP.LABEL.COUPON_CODE') }}
              </label>
              <div class="d-flex" :class="{ 'is-invalid': couponError }">
                <input
                  type="text"
                  class="form-control"
                  :class="{ 'is-valid': coupon }"
                  name="coupon"
                  id="coupon"
                  v-model.trim="couponCode"
                />
                <span
                  class="glyphicon glyphicon-ok form-control-feedback"
                  aria-hidden="true"
                ></span>
                <HMButton
                  class="ml-4"
                  :disabled="!couponCode"
                  color="outline-light-gray"
                  @click="applyCoupon"
                  :loading="isCheckingCoupon"
                >
                  {{ $t('SIGNUP.BUTTON.APPLY') }}
                </HMButton>
              </div>
              <span class="invalid-feedback" v-if="couponError">
                {{ couponError }}
              </span>
            </div>
            <hr class="my-4" />
            <div
              class="summary-container border rounded d-block d-lg-none"
              v-if="getSelectedPriceOption"
            >
              <div class="summary-section">
                <h4 class="py-3 m-0">{{ $t('PRODUCTS.LABELS.SUMMARY') }}</h4>
              </div>
              <hr class="p-0 m-0" />
              <div
                class="summary-section coupon"
                v-if="coupon && getSelectedPriceOption.isDiscountable"
              >
                <div v-html="sanitize(getCheckoutDiscountText)"></div>
                <div v-html="sanitize(discountDurationValidForText)"></div>
              </div>
              <hr class="p-0 m-0" v-if="coupon" />
              <div
                v-if="getSelectedPriceOption.tax"
                class="summary-section tax-info d-flex flex-row justify-content-between align-items-start py-3"
              >
                <span>{{ product.tax_rate_name }}</span>
                <span class="c-charcoal">
                  {{ getTaxText }}
                </span>
              </div>
              <hr v-if="getSelectedPriceOption.tax" class="mt-0" />
              <div
                class="summary-section total d-flex flex-row justify-content-between align-items-start flex-wrap"
              >
                <span>{{ $t('PRODUCTS.LABELS.TOTAL') }}:</span>
                <div class="d-flex flex-column text-right">
                  <div
                    class="h2 c-charcoal m-0"
                    v-html="sanitize(getPriceTranslation)"
                  ></div>
                  <div
                    v-if="getCheckoutNonDiscountedPriceThereafterText"
                    class="small mt-2"
                    v-html="
                      sanitize(getCheckoutNonDiscountedPriceThereafterText)
                    "
                  ></div>
                  <div
                    v-if="getSelectedPriceOption.isPaymentPlan"
                    class="small mt-2"
                    v-html="sanitize(getPaymentPlanThereafterText)"
                  ></div>
                </div>
              </div>
            </div>
            <div class="checkout-button-container mt-4">
              <div
                v-if="isStripeProcessor || isForeverFree"
                class="col-md-12 p-0"
                v-tooltip="{
                  content: checkoutButtonDisabledText,
                  delay: 100,
                  placement: 'top',
                  container: 'body',
                  trigger: 'click hover',
                }"
              >
                <HMButton
                  class="w-100"
                  color="brand"
                  type="submit"
                  :class="{
                    disabled: disabledCheckout || invalid,
                  }"
                  :loading="inProgress"
                >
                  <span class="w-100 text-center">{{
                    getPurchaseButtonText
                  }}</span>
                </HMButton>
              </div>
              <!--              <div v-if="isPayPalProcessor && !isForeverFree">-->
              <!--                <div class="mt20">-->
              <!--                  <form-->
              <!--                    name="_xclick"-->
              <!--                    :action="getPayPalCheckoutUrl"-->
              <!--                    method="post"-->
              <!--                  >-->
              <!--                    <input-->
              <!--                      type="hidden"-->
              <!--                      name="business"-->
              <!--                      :value="productForPurchase.paypal_email"-->
              <!--                    />-->
              <!--                    <input-->
              <!--                      type="hidden"-->
              <!--                      name="currency_code"-->
              <!--                      :value="productForPurchase.currency || 'USD'"-->
              <!--                    />-->
              <!--                    <input-->
              <!--                      type="hidden"-->
              <!--                      name="item_name"-->
              <!--                      :value="productForPurchase.product_name"-->
              <!--                    />-->
              <!--                    <input-->
              <!--                      type="hidden"-->
              <!--                      name="notify_url"-->
              <!--                      :value="getPayPalNotifyUrl"-->
              <!--                    />-->
              <!--                    <input-->
              <!--                      type="hidden"-->
              <!--                      name="return"-->
              <!--                      :value="getPayPalReturnUrl"-->
              <!--                    />-->
              <!--                    <input-->
              <!--                      type="hidden"-->
              <!--                      name="custom"-->
              <!--                      :value="getPayPalCustomParametersField"-->
              <!--                    />-->
              <!--                    <input type="hidden" name="no_shipping" value="1" />-->
              <!--                    <div v-if="!getSelectedPriceOption.isRecurring">-->
              <!--                      <input type="hidden" name="cmd" value="_xclick" />-->
              <!--                      <input type="hidden" name="amount" :value="getPrice" />-->
              <!--                    </div>-->
              <!--                    <div v-if="getSelectedPriceOption.isRecurring">-->
              <!--                      <div v-if="productForPurchase.trial">-->
              <!--                        <input type="hidden" name="a1" value="0" />-->
              <!--                        <input-->
              <!--                          type="hidden"-->
              <!--                          name="p1"-->
              <!--                          :value="productForPurchase.trial_units"-->
              <!--                        />-->
              <!--                        <input-->
              <!--                          type="hidden"-->
              <!--                          name="t1"-->
              <!--                          :value="productForPurchase.trial_period"-->
              <!--                        />-->
              <!--                      </div>-->
              <!--                      <input-->
              <!--                        type="hidden"-->
              <!--                        name="cmd"-->
              <!--                        value="_xclick-subscriptions"-->
              <!--                      />-->
              <!--                      <input-->
              <!--                        type="hidden"-->
              <!--                        name="a3"-->
              <!--                        :value="formatPayPalPrice(getPrice)"-->
              <!--                      />-->
              <!--                      <input type="hidden" name="p3" value="1" />-->
              <!--                      <input-->
              <!--                        type="hidden"-->
              <!--                        name="t3"-->
              <!--                        :value="getSelectedPriceOption.payPalPeriod"-->
              <!--                      />-->
              <!--                      <input type="hidden" name="src" value="1" />-->
              <!--                    </div>-->
              <!--                    <div-->
              <!--                      v-tooltip="{-->
              <!--                        content: checkoutButtonDisabledText,-->
              <!--                        delay: 100,-->
              <!--                        placement: 'top',-->
              <!--                        container: 'body',-->
              <!--                        trigger: 'click hover',-->
              <!--                      }"-->
              <!--                    >-->
              <!--                      <HMButton-->
              <!--                        class="w-100"-->
              <!--                        color="paypal"-->
              <!--                        type="submit"-->
              <!--                        :disabled="disabledCheckout"-->
              <!--                      >-->
              <!--                        <div class="w-100">-->
              <!--                          <span>{{-->
              <!--                            $t('PRODUCTS.LABELS.CHECK_OUT_WITH')-->
              <!--                          }}</span>-->
              <!--                          <img-->
              <!--                            :src="require('@/assets/images/PayPal.png')"-->
              <!--                            class="ml-2 img-paypal"-->
              <!--                          />-->
              <!--                        </div>-->
              <!--                      </HMButton>-->
              <!--                    </div>-->
              <!--                  </form>-->
              <!--                </div>-->
              <!--              </div>-->
            </div>
          </div>
          <div class="mt-4 footer-info">
            <div class="d-block d-md-none">
              <hr class="mt-3" />
              <div
                class="d-flex d-md-none flex-row justify-content-between align-items-center flex-wrap"
              >
                <div
                  class="credit-cards d-flex flex-row justify-content-start flex-nowrap mb-2"
                >
                  <img
                    class="img-cc mr-2"
                    :src="require(`@/assets/images/Visa.png`)"
                  />
                  <img
                    class="img-cc mr-2"
                    :src="require(`@/assets/images/Amex.png`)"
                  />
                  <img
                    class="img-cc mr-2"
                    :src="require(`@/assets/images/Mastercard.png`)"
                  />
                  <img
                    class="img-cc mr-2"
                    :src="require(`@/assets/images/cartes-bancaires.png`)"
                  />
                </div>
                <div class="d-flex">
                  <span>{{ $t('PRODUCTS.LABELS.CHECKOUT_BY') }}</span>
                  <img
                    v-if="isPayPalProcessor"
                    class="img-paypal ml-2"
                    :src="require(`@/assets/images/PayPal.png`)"
                  />
                  <img
                    v-if="isStripeProcessor"
                    class="img-stripe ml-2"
                    :src="require(`@/assets/images/Stripe.png`)"
                  />
                </div>
              </div>
              <hr class="mt-2" />
            </div>
            <div class="mt-4">
              <font-awesome-icon
                :icon="['far', 'lock-alt']"
                class="mr-1 text-primary c-brand-color"
              />
              {{ $t('SIGNUP.TEXT.THIS_IS_A_SECURE_256_BIT') }}
              <span class="c-charcoal">{{ $t('SIGNUP.TEXT.100_SECURE') }}</span>
            </div>
            <div class="filler d-none d-sm-block"></div>
            <div
              class="d-none d-md-flex flex-row flex-wrap justify-content-between align-items-center mt-4"
            >
              <div
                class="credit-cards d-flex flex-row justify-content-start flex-nowrap"
              >
                <img
                  class="img-cc mr-2"
                  :src="require(`@/assets/images/Visa.png`)"
                />
                <img
                  class="img-cc mr-2"
                  :src="require(`@/assets/images/Amex.png`)"
                />
                <img
                  class="img-cc mr-2"
                  :src="require(`@/assets/images/Mastercard.png`)"
                />
                <img
                  class="img-cc mr-2"
                  :src="require(`@/assets/images/cartes-bancaires.png`)"
                />
              </div>
              <div class="d-flex">
                <span>{{ $t('PRODUCTS.LABELS.CHECKOUT_BY') }}</span>
                <img
                  v-if="isPayPalProcessor"
                  class="img-paypal ml-2"
                  :src="require(`@/assets/images/PayPal.png`)"
                />
                <img
                  v-if="isStripeProcessor"
                  class="img-stripe ml-2"
                  :src="require(`@/assets/images/Stripe.png`)"
                />
              </div>
            </div>

            <div class="d-block d-lg-none">
              <hr class="mt-4" />
              <div
                class="d-flex flex-row justify-content-between align-items-center flex-wrap mt-3"
              >
                <h3 class="mr-2">{{ $t('PRODUCTS.LABELS.NEED_ANY_HELP') }}</h3>
                <a
                  class="undecorated-router-link d-block"
                  :href="`mailto:${studio.email}`"
                >
                  <span>
                    <i class="fal fa-comments-alt c-brand-color"></i>
                    {{ $t('STUDIO.LABELS.CONTACT_US') }}
                  </span>
                </a>
              </div>
              <hr />
            </div>

            <span
              class="d-block mt-2 smaller my-5"
              v-html="sanitize($t('SIGNUP.TEXT.THIS_SITE_IS_PROTECTED_BY'))"
            ></span>
          </div>
        </div>
      </div>
      <div
        class="checkout-summary-desktop d-none d-lg-block offset-lg-1 col-md-4 col-lg-4 bg-lightest-gray full-height"
        v-if="product && getSelectedPriceOption"
      >
        <div class="position-fixed ml-2 ml-xl-4 mr-2 mr-xl-4 h-100">
          <div
            class="d-flex flex-column justify-content-between align-items-start h-100"
          >
            <div class="top-section">
              <div
                class="product-section d-flex flex-row justify-content-start align-items-start flex-nowrap mt-4"
              >
                <HMFixedAspectRatioContainer class="img-container">
                  <ProductThumbnail :product="product" :hideOverlay="true" />
                </HMFixedAspectRatioContainer>
                <div
                  class="d-flex flex-column justify-content-start align-items-start ml-2 mt-2"
                >
                  <span class="c-charcoal">{{ product.product_name }}</span>
                  <span
                    class="smaller mt-2"
                    :class="{
                      'price-line-through': wishPriceSelected,
                    }"
                    v-html="
                      sanitize(
                        getSelectedPriceOption.selectedTitle ||
                          getSelectedPriceOption.html
                      )
                    "
                  ></span>
                </div>
              </div>
              <hr />
              <div class="coupon-code">
                <div
                  class="form-group coupon-form mb-3"
                  v-if="canShowCouponCode"
                >
                  <label for="coupon">
                    {{ $t('SIGNUP.LABEL.COUPON_CODE') }}
                  </label>
                  <div class="d-flex" :class="{ 'is-invalid': couponError }">
                    <input
                      type="text"
                      class="form-control"
                      :class="{ 'is-valid': coupon }"
                      name="coupon"
                      id="coupon"
                      v-model.trim="couponCode"
                    />
                    <span
                      class="glyphicon glyphicon-ok form-control-feedback"
                      aria-hidden="true"
                    ></span>
                    <HMButton
                      class="ml-4"
                      :disabled="!couponCode"
                      color="outline-light-gray"
                      @click="applyCoupon"
                      :loading="isCheckingCoupon"
                    >
                      {{ $t('SIGNUP.BUTTON.APPLY') }}
                    </HMButton>
                  </div>
                  <span class="invalid-feedback" v-if="couponError">
                    {{ couponError }}
                  </span>
                </div>
                <div
                  v-if="coupon && canShowCouponCode"
                  class="mt-1 coupon-info"
                >
                  <div class="text-right">
                    <div v-html="sanitize(getCheckoutDiscountText)"></div>
                    <div v-html="sanitize(discountDurationValidForText)"></div>
                  </div>
                </div>
                <hr v-if="canShowCouponCode" />
                <div
                  v-if="getSelectedPriceOption.tax"
                  class="tax-info d-flex flex-row justify-content-between align-items-start mt-2"
                >
                  <span>{{ product.tax_rate_name }}</span>
                  <span class="c-charcoal">
                    {{ getTaxText }}
                  </span>
                </div>
                <hr v-if="getSelectedPriceOption.tax" />
                <div
                  class="checkout-summary d-flex flex-row justify-content-between align-items-start mt-2"
                >
                  <span>{{ $t('PRODUCTS.TITLE.SUMMARY') }}:</span>
                  <div class="d-flex flex-column text-right">
                    <div
                      class="h2 c-charcoal m-0"
                      v-html="sanitize(getPriceTranslation)"
                    ></div>
                    <div
                      v-if="getCheckoutNonDiscountedPriceThereafterText"
                      class="small mt-2"
                      v-html="
                        sanitize(getCheckoutNonDiscountedPriceThereafterText)
                      "
                    ></div>
                    <div
                      v-if="getSelectedPriceOption.isPaymentPlan"
                      class="small mt-2"
                      v-html="sanitize(getPaymentPlanThereafterText)"
                    ></div>
                  </div>
                </div>
              </div>
            </div>
            <div class="bottom-section footer-info w-100">
              <div
                class="d-flex flex-row justify-content-between align-items-center flex-wrap"
              >
                <h3 class="mr-2">{{ $t('PRODUCTS.LABELS.NEED_ANY_HELP') }}</h3>
                <a
                  class="undecorated-router-link d-block"
                  :href="`mailto:${studio.email}`"
                >
                  <span>
                    <i class="fal fa-comments-alt c-brand-color"></i>
                    {{ $t('STUDIO.LABELS.CONTACT_US') }}
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </ValidationObserver>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import { errorToMessage, scrollToFirstFormError } from '@/core/utils/errors';
import auth from '@/core/api/modules/auth';
import { trackFBPixelStudioPurchase } from '@/web/services/analytics';
import StudioCheckout from '@/web/components/checkout/StudioCheckout.vue';
import GiftPurchasedModal from '@/web/components/checkout/GiftPurchasedModal.vue';
import { ValidationObserver } from 'vee-validate';
import { sanitize } from '@/core/utils/utils';
import { style } from '@/web/utils/stripe';

const RECURRING_OPTIONS = {
  annually: {
    order: 0,
    translation: 'PRODUCTS.LABELS.ANNUAL_SUBSCRIPTION_GIFT_VARIANT',
    priceTranslation: 'PRODUCTS.LABELS.PRICE_WITHOUT_DURATION',
    label: 'year',
    payPalPeriod: 'Y',
    billingInterval: 1,
  },
  biannually: {
    order: 1,
    translation: 'PRODUCTS.LABELS.BIANNUAL_SUBSCRIPTION_GIFT_VARIANT',
    priceTranslation: 'PRODUCTS.LABELS.PRICE_WITHOUT_DURATION',
    label: 'month',
    payPalPeriod: 'M',
    billingInterval: 6,
  },
  quarterly: {
    order: 2,
    translation: 'PRODUCTS.LABELS.QUARTERLY_SUBSCRIPTION_GIFT_VARIANT',
    priceTranslation: 'PRODUCTS.LABELS.PRICE_WITHOUT_DURATION',
    label: 'month',
    payPalPeriod: 'M',
    billingInterval: 3,
  },
  monthly: {
    order: 3,
    translation: 'PRODUCTS.LABELS.MONTHLY_SUBSCRIPTION_GIFT_VARIANT',
    priceTranslation: 'PRODUCTS.LABELS.PRICE_WITHOUT_DURATION',
    label: 'month',
    payPalPeriod: 'M',
    billingInterval: 1,
  },
  weekly: {
    order: 4,
    translation: 'PRODUCTS.LABELS.WEEKLY_SUBSCRIPTION_GIFT_VARIANT',
    priceTranslation: 'PRODUCTS.LABELS.PRICE_WITHOUT_DURATION',
    label: 'week',
    payPalPeriod: 'W',
    billingInterval: 1,
  },
  daily: {
    order: 5,
    translation: 'PRODUCTS.LABELS.DAILY_SUBSCRIPTION_GIFT_VARIANT',
    priceTranslation: 'PRODUCTS.LABELS.PRICE_WITHOUT_DURATION',
    label: 'day',
    payPalPeriod: 'D',
    billingInterval: 1,
  },
};

export default {
  name: 'StudioGiftCheckout',
  extends: StudioCheckout,
  components: {
    ValidationObserver,
  },
  data() {
    return {
      recipient: {
        firstName: '',
        lastName: '',
        email: '',
        message: '',
      },
      gifter: {
        anonymous: false,
        firstName: '',
        lastName: '',
        email: '',
      },
      giftDate: {},
      continueAsGuest: false,
      unitsOfPurchase: 1,
      inProgress: false,
      stripeError: false,
      stripeCompleted: false,
      stripe: null,
    };
  },
  computed: {
    ...mapGetters({
      student: 'user/student',
      user: 'user/user',
    }),
    priceOptions() {
      const options = [];
      if (this.isRecurring) {
        if (this.isRecurringMultiple) {
          const recurringOptions = Object.keys(
            this.product.recurring_options
          ).sort((o1, o2) => {
            return (
              RECURRING_OPTIONS[o1.toLowerCase()].order -
              RECURRING_OPTIONS[o2.toLowerCase()].order
            );
          });
          recurringOptions.forEach(option => {
            option = option.toLowerCase();
            if (this.product.recurring_options[option].available === true) {
              const originalPrice = parseFloat(
                this.product.recurring_options[option].price || 0
              );
              const discountedPrice = this.getCouponDiscountPrice(
                originalPrice
              );
              const originalPriceTax = this.getTax(originalPrice);
              const tax = this.getTax(discountedPrice);
              const taxWithCurrency = this.$formatCurrency(
                tax || 0,
                this.currency
              );
              const finalPrice = discountedPrice + tax;
              const price = this.$formatCurrency(
                finalPrice || 0,
                this.currency
              );
              const originalPriceWithCurrency = this.$formatCurrency(
                originalPrice || 0,
                this.currency
              );
              const originalPriceWithTax = originalPrice + originalPriceTax;
              const originalPriceWithTaxWithCurrency = this.$formatCurrency(
                originalPriceWithTax || 0,
                this.currency
              );
              const wishPriceTax = this.getTax(
                this.product.recurring_options[option].min_wish_price
              );
              const wishPriceAmount = parseFloat(
                this.product.recurring_options[option].min_wish_price
              );
              const wishPrice = this.$formatCurrency(
                wishPriceAmount + wishPriceTax,
                this.currency
              );
              const recurringOption = RECURRING_OPTIONS[option];
              options.push({
                html: sanitize(
                  this.$tc(
                    recurringOption.translation,
                    recurringOption.billingInterval,
                    {
                      count: recurringOption.billingInterval,
                      price: originalPriceWithCurrency,
                    }
                  )
                ),
                value: option,
                originalPriceTranslation: this.$t(
                  recurringOption.priceTranslation,
                  {
                    price: originalPriceWithCurrency,
                  }
                ),
                originalPriceWithTaxTranslation: this.$t(
                  recurringOption.priceTranslation,
                  {
                    price: originalPriceWithTaxWithCurrency,
                  }
                ),
                priceTranslation: this.$t(recurringOption.priceTranslation, {
                  price,
                }),
                priceTranslationKey: recurringOption.priceTranslation,
                originalPrice: originalPrice,
                price: finalPrice,
                wishPrice: wishPrice,
                wishPriceAmount: wishPriceAmount,
                netPrice: discountedPrice,
                tax: tax,
                taxWithCurrency: taxWithCurrency,
                isRecurring: true,
                isMultipleRecurring: true,
                payPalPeriod: recurringOption.payPalPeriod,
                billingInterval: recurringOption.billingInterval,
                isDiscountable: true,
              });
            }
          });
        } else {
          const originalPrice = parseFloat(this.product.price || 0);
          const discountedPrice = this.getCouponDiscountPrice(originalPrice);

          const originalPriceTax = this.getTax(originalPrice);
          const tax = this.getTax(discountedPrice);
          const taxWithCurrency = this.$formatCurrency(tax || 0, this.currency);
          const finalPrice = discountedPrice + tax;
          const price = this.$formatCurrency(finalPrice, this.currency);
          const originalPriceWithCurrency = this.$formatCurrency(
            originalPrice || 0,
            this.currency
          );

          const originalPriceWithTax = originalPrice + originalPriceTax;
          const originalPriceWithTaxWithCurrency = this.$formatCurrency(
            originalPriceWithTax || 0,
            this.currency
          );

          const wishPriceTax = this.getTax(this.product.min_wish_price);
          const wishPriceAmount = parseFloat(this.product.min_wish_price);
          const wishPrice = this.$formatCurrency(
            wishPriceAmount + wishPriceTax,
            this.currency
          );

          const recurringOption =
            RECURRING_OPTIONS[this.product.recurring_type];
          options.push({
            html: sanitize(
              this.$tc(
                recurringOption.translation,
                recurringOption.billingInterval,
                {
                  count: recurringOption.billingInterval,
                  price: originalPriceWithCurrency,
                }
              )
            ),
            value: this.product.recurring_type,
            originalPriceTranslation: this.$t(
              recurringOption.priceTranslation,
              {
                price: originalPriceWithCurrency,
              }
            ),
            originalPriceWithTaxTranslation: this.$t(
              recurringOption.priceTranslation,
              {
                price: originalPriceWithTaxWithCurrency,
              }
            ),
            priceTranslation: this.$t(recurringOption.priceTranslation, {
              price,
            }),
            priceTranslationKey: recurringOption.priceTranslation,
            originalPrice: originalPrice,
            price: finalPrice,
            wishPrice: wishPrice,
            wishPriceAmount: wishPriceAmount,
            netPrice: discountedPrice,
            tax: tax,
            taxWithCurrency: taxWithCurrency,
            isRecurring: true,
            payPalPeriod: recurringOption.payPalPeriod,
            billingInterval: recurringOption.billingInterval,
            isDiscountable: true,
          });
        }
      }
      if (this.isFixedPrice) {
        const originalPrice = parseFloat(this.product.price || 0);
        const discountedPrice = this.getCouponDiscountPrice(originalPrice);
        const originalPriceTax = this.getTax(originalPrice);
        const tax = this.getTax(discountedPrice);
        const taxWithCurrency = this.$formatCurrency(tax || 0, this.currency);
        const finalPrice = discountedPrice + tax;
        const price = this.$formatCurrency(finalPrice, this.currency);
        const originalPriceWithCurrency = this.$formatCurrency(
          originalPrice || 0,
          this.currency
        );
        const originalPriceWithTax = originalPrice + originalPriceTax;
        const originalPriceWithTaxWithCurrency = this.$formatCurrency(
          originalPriceWithTax || 0,
          this.currency
        );

        const wishPriceTax = this.getTax(this.product.min_wish_price);
        const wishPriceAmount = parseFloat(this.product.min_wish_price);
        const wishPrice = this.$formatCurrency(
          wishPriceAmount + wishPriceTax,
          this.currency
        );

        options.push({
          html: sanitize(
            this.$t('PRODUCTS.LABELS.ONE_TIME', {
              price: originalPriceWithCurrency,
            })
          ),
          value: 'fixed',
          originalPriceTranslation: `${originalPriceWithCurrency}`,
          originalPriceWithTaxTranslation: `${originalPriceWithTaxWithCurrency}`,
          priceTranslation: `${price}`,
          originalPrice: originalPrice,
          price: finalPrice,
          wishPrice: wishPrice,
          wishPriceAmount: wishPriceAmount,
          netPrice: discountedPrice,
          tax: tax,
          taxWithCurrency: taxWithCurrency,
          isRecurring: false,
          isDiscountable: true,
        });
      }
      if (this.product.day_pass_enabled) {
        const originalPrice = parseFloat(this.product.day_pass_price || 0);
        const discountedPrice = this.getCouponDiscountPrice(originalPrice);
        const originalPriceTax = this.getTax(originalPrice);
        const tax = this.getTax(discountedPrice);
        const taxWithCurrency = this.$formatCurrency(tax || 0, this.currency);
        const finalPrice = discountedPrice + tax;
        const price = this.$formatCurrency(finalPrice, this.currency);
        const originalPriceWithCurrency = this.$formatCurrency(
          originalPrice || 0,
          this.currency
        );
        const wishPriceTax = this.getTax(this.product.min_wish_price);
        const wishPriceAmount = parseFloat(this.product.min_wish_price);
        const wishPrice = this.$formatCurrency(
          wishPriceAmount + wishPriceTax,
          this.currency
        );
        options.push({
          html: sanitize(
            this.$t('PRODUCTS.LABELS.SINGLE_DAY_ACCESS_PASS', {
              price: originalPriceWithCurrency,
            })
          ),
          value: 'daypass',
          originalPriceTranslation: `${originalPriceWithCurrency}`,
          priceTranslation: `${price}`,
          originalPrice: originalPrice,
          price: finalPrice,
          wishPrice: wishPrice,
          wishPriceAmount: wishPriceAmount,
          netPrice: discountedPrice,
          tax: tax,
          taxWithCurrency: taxWithCurrency,
          isRecurring: false,
          isDaypass: true,
          isDiscountable: true,
        });
      }
      return options;
    },
    giftDatInvalid() {
      const isFixedSchedule = this.product?.product_schedule_type === 'fixed';
      const endDate = this.product?.product_drip_end_date;
      const giftDate = this.giftDate.startDate;
      if (isFixedSchedule && endDate && giftDate) {
        return moment(giftDate).isAfter(endDate);
      }
      return false;
    },
    getPurchaseButtonText() {
      return this.$t('PRODUCTS.BUTTON.PURCHASE_GIFT');
    },
    getCheckoutNonDiscountedPriceThereafterText() {
      return '';
    },
    isForeverFree() {
      if (this.isFreePrice) return true;
      if (!this.getSelectedPriceOption) return false;
      if (this.getSelectedPriceOption.price > 0) return false;

      if (this.coupon) return true;

      return false;
    },
    disabledCheckout() {
      if (this.isFreeCheckout) return false;
      if (this.inProgress) return true;

      if (this.isStripeProcessor) {
        if (this.useExistingCustomerData) return false;
        if (this.stripeCompleted) return false;
      }

      return true;
    },
    checkoutButtonDisabledText() {
      if (!this.isLoggedIn && !this.continueAsGuest) {
        return this.$t(
          'PRODUCTS.TEXT.YOU_NEED_TO_LOGIN_OR_CREATE_AN_ACCOUNT_FIRST'
        );
      }
      if (this.inProgress) {
        return '';
      }
      if (this.disabledCheckout) {
        return this.$t(
          'PRODUCTS.TEXT.BUTTON_WILL_BE_ENABLED_WHEN_CREDIT_CARD_INFORMATION_IS_ENTERED'
        );
      }
      return '';
    },
    getGiftPayload() {
      return {
        gifter_first_name: this.gifter.firstName,
        gifter_last_name: this.gifter.lastName,
        gifter_email: this.gifter.email,
        recipient_first_name: this.recipient.firstName,
        recipient_last_name: this.recipient.lastName,
        recipient_email: this.recipient.email,
        is_anonymous: this.gifter.anonymous,
        message: this.recipient.message,
        send_at: this.giftDate.startDate
          ? this.giftDate.startDate.toISOString()
          : null,
      };
    },
  },
  methods: {
    sanitize,
    initStripe() {
      this.stripe = window.Stripe(
        this.productForPurchase.stripe_publishable_key
      );
      const elements = this.stripe.elements();
      this.card = elements.create('card', {
        style,
        hidePostalCode: true,
      });
      this.card.addEventListener('change', this.stripeChangeCallback);
      const mountTimer = setInterval(
        () => {
          console.log('Waiting for stripe card element');
          if (this.$refs['card-element']) {
            this.card.mount(this.$refs['card-element']);
            console.log('Stripe card element mounted');
            clearInterval(mountTimer);
          }
        },
        50,
        100
      );
    },
    openGiftPurchasedModal() {
      this.$modal.show(
        GiftPurchasedModal,
        {
          email: this.recipient.email,
          closeFn: () => {
            this.$router.replace({ name: 'studio-home' });
          },
        },
        {
          clickToClose: false,
        }
      );
    },
    async purchase() {
      this.$refs.form.validate();
      const invalid = this.$refs.form?.flags?.invalid;
      if (invalid) {
        setTimeout(() => {
          scrollToFirstFormError();
        });
      }

      if (this.disabledCheckout || invalid) {
        return;
      }

      this.inProgress = true;
      let reCaptchaToken;
      try {
        const reCaptchaKey = process.env.VUE_APP_RECAPTCHA_KEY;
        reCaptchaToken = await window.grecaptcha.execute(reCaptchaKey, {
          action: 'buyproduct',
        });
        //await auth.captchaVerify(reCaptchaToken);
      } catch (error) {
        console.log('error=', error);
        this.inProgress = false;
        this.$notify({
          group: 'app',
          type: 'error',
          text: this.$t('GENERAL.ERROR.RECAPTCHA_FAILED'),
          duration: -1, // stay ON
        });
        return;
      }

      const coupon = this.coupon?.code;
      const studioURL = this.studioURL;
      const productId = this.productId;
      const selectedPriceOption = this.getSelectedPriceOption;
      const recurringOption = selectedPriceOption.isMultipleRecurring
        ? selectedPriceOption.value
        : null;
      const daypass = selectedPriceOption.isDaypass;
      const isPaymentPlan = selectedPriceOption.isPaymentPlan;
      const paymentPlanId = selectedPriceOption.paymentPlan?.id;
      const customAmount = this.wishPriceSelected;

      const purchasePayload = {
        coupon,
        studioURL,
        productId,
        recurringOption,
        daypass,
        isPaymentPlan,
        paymentPlanId,
        customAmount,
        reCaptchaToken,
      };

      if (!this.isForeverFree) {
        if (this.useExistingCustomerData) {
          purchasePayload.defaultPaymentMethod = this.existingCreditCardPaymentId;
          purchasePayload.stripeCustomerId = this.stripeCustomerData?.id;
        } else {
          try {
            const setupIntent = await this.$store.dispatch(
              'product/createGiftStripeSetupIntent',
              {
                studioURL,
                customerEmail: this.gifter.email,
              }
            );
            const stripeCustomerId = setupIntent.stripe_customer_id;

            try {
              const confirmCard = await this.stripe.confirmCardSetup(
                setupIntent.setup_intent.client_secret,
                { payment_method: { card: this.card } }
              );
              if (!confirmCard.setupIntent) {
                this.$notify({
                  group: 'app',
                  type: 'error',
                  title: 'Stripe card authorization failed',
                  duration: -1, // stay ON
                });
                this.inProgress = false;
                return;
              }
              const defaultPaymentMethod =
                confirmCard.setupIntent.payment_method;

              purchasePayload.defaultPaymentMethod = defaultPaymentMethod;
              purchasePayload.stripeCustomerId = stripeCustomerId;
            } catch (error) {
              this.$notify({
                group: 'app',
                type: 'error',
                title: 'Stripe card authorization failed',
                text: errorToMessage(
                  error.response,
                  `Request failed: ${error}`
                ),
                duration: -1, // stay ON
              });
              this.inProgress = false;
              return;
            }
          } catch (error) {
            this.$notify({
              group: 'app',
              type: 'error',
              title: 'Stripe error',
              text: errorToMessage(error.response, `Request failed: ${error}`),
              duration: -1, // stay ON
            });
            this.inProgress = false;
            return;
          }
        }
      }
      const payload = {
        ...purchasePayload,
        giftPayload: this.getGiftPayload,
        rewardfulReferralId: window.Rewardful?.referral,
      };

      try {
        const purchaseResult = await this.$store.dispatch(
          'product/purchaseProductAsGift',
          payload
        );
        this.$store.dispatch('studio/getStudio', this.studioURL);

        trackFBPixelStudioPurchase(
          this.studio.fb_pixel_id,
          this.fbPixelPurchasePayload
        );

        this.inProgress = false;
        this.openGiftPurchasedModal();
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Purchase failed',
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      } finally {
        this.inProgress = false;
      }
    },
  },
  mounted() {
    const { first_name, last_name } = this.student;
    const { email } = this.user;
    this.gifter.firstName = first_name;
    this.gifter.lastName = last_name;
    this.gifter.email = email;
  },
  watch: {
    productForPurchase: {
      handler: function (productForPurchase) {
        if (productForPurchase) {
          if (this.isStripeProcessor) {
            this.$nextTick(() => {
              this.initStripe();
            });
          }
        }
      },
      immediate: true,
    },
    student: {
      handler: function (student) {
        if (student) {
          this.gifter.firstName = student.first_name;
          this.gifter.lastName = student.last_name;
        }
      },
      immediate: true,
    },
    user: {
      handler: function (user) {
        if (user) {
          this.gifter.email = user.email;
        }
      },
      immediate: true,
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.studio-product-checkout {
}

.price-option-container {
  padding: 1rem;
  border: 1px solid $medium-gray;
  border-radius: 6px;
  margin-bottom: 1rem;
}

.img-paypal {
  height: 22px;
  width: auto;
}

.img-stripe {
  height: 22px;
  width: auto;
}

.img-cc {
  height: 40px;
  width: auto;

  @include media-breakpoint-down(xs) {
    height: 30px;
    width: auto;
  }
}

.full-height {
  min-height: 100vh;
}

.img-container {
  flex-shrink: 0;
  width: 150px;
  &.mobile {
    @include media-breakpoint-only(xs) {
      width: 100%;
    }
  }
}

.mobile-product-container {
  margin-left: -15px;
  margin-right: -15px;
}

.summary-section {
  padding: 0.5rem 1rem;
}

.checkout-button-container {
  @include media-breakpoint-only(xs) {
    position: fixed;
    bottom: 0;
    left: 0;
    background-color: white;
    height: 70px;
    width: 100%;
    z-index: 1031;
    padding: 1rem 15px;
    box-shadow: 0px -1px 5px rgba(50, 50, 50, 0.2);
  }
}

.filler {
  height: 40px;
}

.bottom-section {
  padding-bottom: 4rem;
}

.calendar-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: var(--brand-color-with-opacity-03, $coral);
  color: var(--brand-color, $coral);
}
.remove-icon {
  padding-top: 2px;
}
</style>

<style lang="scss">
@import '@/web/styles/components-variables.scss';

.price-line-through .c-charcoal {
  text-decoration: line-through;
}
#pay-what-you-wish::placeholder {
  @include media-breakpoint-down(sm) {
    font-size: 12px;
  }
}
</style>
